import { ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormGroupComponent, InputComponent, ITab, TabsComponent } from '@tecalisdev/infinity';

import { LanguageService } from '../../../../core/services/language.service';
import { ReactiveFormsService } from '../../../../shared/services/reactive-forms.service';
import { firstMissingLocaleTextId } from '../../../utils/workflow-locale-text.util';

export type FormStepInstructionsVariant = 'locked' | 'editable';

type FormStepLocaleFieldName = 'step_name' | 'step_description';

@Component({
	selector: 'app-form-step-instructions',
	imports: [ReactiveFormsModule, TranslatePipe, FormGroupComponent, InputComponent, TabsComponent],
	templateUrl: './form-step-instructions.component.html',
	styleUrl: './form-step-instructions.component.scss',
})
export class FormStepInstructionsComponent implements OnInit {
	public reactiveFormService = inject(ReactiveFormsService);
	private trans = inject(TranslateService);
	private languageService = inject(LanguageService);
	private changeDetectorRef = inject(ChangeDetectorRef);

	@Input({ required: true }) form!: FormGroup;
	@Input() variant: FormStepInstructionsVariant = 'editable';
	@Input() showSectionHeader = true;

	public stepLocaleTabs: ITab[] = [];
	public selectedStepLocaleId = 'es';

	ngOnInit(): void {
		this.buildStepLocaleTabs();
	}

	public onStepLocaleTabSelected(tab: { id: string; label: string }): void {
		const localeId = tab?.id != null ? String(tab.id) : '';
		if (!localeId) {
			return;
		}
		this.selectLocaleTab(localeId);
	}

	/**
	 * Resuelve el FormControl del idioma activo recorriendo la ruta del formulario.
	 * Evita `[formControlName]` dinámico, que no desvincula bien al cambiar de pestaña.
	 */
	public getLocaleFormControl(...formPathSegments: string[]): FormControl {
		let control: AbstractControl | null = this.form;
		for (const segment of formPathSegments) {
			control = control?.get(segment) ?? null;
		}
		return control as FormControl;
	}

	/** Marca como touched los grupos multi-idioma de pauta y descripción (y sus hijos). */
	public markLocaleFieldsTouched(): void {
		for (const fieldName of ['step_name', 'step_description'] as const) {
			const localeTextGroup = this.form.get(fieldName) as FormGroup | null;
			localeTextGroup?.markAllAsTouched();
		}
	}

	/**
	 * Igual que `modal-doc-form.focusDocLocaleOnInvalid`: salta a la pestaña del primer idioma
	 * con error, hace scroll y enfoca el input correspondiente.
	 */
	public focusFirstInvalidLocaleField(): boolean {
		const allowedLocales = this.languageService.getAllowedLanguages();

		for (const fieldName of ['step_name', 'step_description'] as const) {
			const localeTextGroup = this.form.get(fieldName) as FormGroup | null;
			if (!localeTextGroup?.invalid) {
				continue;
			}

			const missingLocaleId = firstMissingLocaleTextId(localeTextGroup, allowedLocales);
			if (!missingLocaleId) {
				continue;
			}

			this.selectLocaleTab(missingLocaleId);

			const anchorId = this.resolveLocaleFieldAnchorId(fieldName);
			setTimeout(() => {
				document.getElementById(anchorId)?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'nearest',
				});
				const anchorElement = document.getElementById(anchorId);
				const focusableInput = anchorElement?.querySelector('input, textarea') as HTMLElement | null;
				focusableInput?.focus?.();
			}, 320);

			return true;
		}

		return false;
	}

	public selectLocaleTabPublic(localeId: string): void {
		this.selectLocaleTab(localeId);
	}

	/** Indica si la pestaña activa debe mostrar estado de error en el campo indicado. */
	public localeFieldHasError(fieldName: FormStepLocaleFieldName): boolean {
		const localeControl = this.getLocaleFormControl(fieldName, this.selectedStepLocaleId);
		if (this.reactiveFormService.hasError(localeControl)) {
			return true;
		}

		const localeTextGroup = this.form.get(fieldName) as FormGroup | null;
		if (!localeTextGroup || !this.reactiveFormService.hasError(localeTextGroup)) {
			return false;
		}

		// Grupo inválido (p. ej. «si uno tiene texto, todos»): error en cada idioma vacío.
		const trimmedValue = (localeControl.value ?? '').toString().trim();
		return trimmedValue.length === 0;
	}

	/** Mensaje de error solo para la pestaña de idioma activa (no mezcla errores de otros idiomas). */
	public localeFieldErrorMessage(fieldName: FormStepLocaleFieldName): string {
		const localeControl = this.getLocaleFormControl(fieldName, this.selectedStepLocaleId);
		const controlErrorMessage = this.reactiveFormService.hasMsgError(localeControl);
		if (controlErrorMessage) {
			return controlErrorMessage;
		}

		if (this.localeFieldHasError(fieldName)) {
			const localeTextGroup = this.form.get(fieldName) as FormGroup | null;
			const groupErrorMessage = this.reactiveFormService.hasMsgError(
				localeTextGroup as unknown as Partial<FormControl>,
			);
			return groupErrorMessage || this.trans.instant('ERRORS.REQUIRED');
		}

		return '';
	}

	private selectLocaleTab(localeId: string): void {
		this.selectedStepLocaleId = localeId;
		this.stepLocaleTabs = this.stepLocaleTabs.map((tabItem) => ({
			...tabItem,
			selected: String(tabItem.id) === localeId,
		}));
		this.changeDetectorRef.detectChanges();
	}

	private resolveLocaleFieldAnchorId(fieldName: FormStepLocaleFieldName): string {
		return fieldName === 'step_name' ? 'form-step-locale-field-guideline' : 'form-step-locale-field-description';
	}

	/** Construye las pestañas ES / EN / PT… según `LanguageService.getAllowedLanguages()`. */
	private buildStepLocaleTabs(): void {
		const allowedLanguages = this.languageService.getAllowedLanguages();
		this.selectedStepLocaleId = allowedLanguages[0] ?? 'es';
		this.stepLocaleTabs = allowedLanguages.map((languageCode) => ({
			id: languageCode,
			label: this.trans.instant(`MASTER.LANGUAGE.${languageCode.toUpperCase()}`),
			selected: languageCode === this.selectedStepLocaleId,
		}));
	}
}
